import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ActivityType } from '@goyone/shared';
import { api } from '../../lib/api';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/ui';
import { RecordPicker, type RecordLink } from '../../components/RecordPicker';
import { ActivityComposer } from './ActivityComposer';

/**
 * إنشاء نشاطٍ من شاشةٍ عامّة — خطوتان في نافذةٍ واحدة.
 *
 * داخل ملف العميل يكون السجلّ معلومًا، أما شاشات «الأنشطة» و«الاجتماعات»
 * فتبدأ بلا صاحب: فيُنتقى العميل (محتملًا كان أو عميلًا أو جهة اتصال) ثم
 * يُملأ النموذج نفسه الذي في الملف — لا نموذجَ ثانٍ يفترق عنه.
 *
 * وموقع العميل يُجلب بعد انتقائه، فيرث الاجتماعُ الحضوريّ خريطتَه كما
 * يرثها حين يُنشأ من داخل الملف.
 */
export function NewActivityModal({
  onClose,
  onDone,
  defaultType,
  title = 'نشاط جديد',
}: {
  onClose: () => void;
  onDone: () => void;
  /** يفتح النموذج على نوعٍ بعينه — «اجتماع جديد» يقصد الاجتماع. */
  defaultType?: ActivityType;
  title?: string;
}) {
  const [link, setLink] = useState<RecordLink | null>(null);
  const [name, setName] = useState('');

  /* موقع صاحب السجلّ — به يمتلئ حقل خريطة الاجتماع الحضوريّ. */
  const owner = useQuery({
    queryKey: ['record-maps', link?.leadId ?? link?.accountId ?? null],
    queryFn: () =>
      api<{ mapsUrl?: string | null }>(
        link?.leadId ? `/crm/leads/${link.leadId}` : `/crm/accounts/${link!.accountId}`,
      ),
    enabled: Boolean(link?.leadId || link?.accountId),
  });

  return (
    <Modal title={link ? `${title} — ${name}` : title} onClose={onClose}>
      {!link ? (
        <RecordPicker
          onPick={(picked, pickedName) => {
            setLink(picked);
            setName(pickedName);
          }}
        />
      ) : (
        <>
          <button
            type="button"
            className="btn btn-sm mb-4"
            onClick={() => {
              setLink(null);
              setName('');
            }}
          >
            <Icon name="arrowLeft" size={14} />
            تغيير السجل
          </button>

          <ActivityComposer
            link={link}
            recordMapsUrl={owner.data?.mapsUrl ?? null}
            defaultType={defaultType}
            /* النوع مقصودٌ سلفًا، فالنموذج يُفتح ولا يُطلب فتحُه بزرٍّ ثانٍ. */
            startOpen={Boolean(defaultType)}
            onDone={onDone}
          />
        </>
      )}
    </Modal>
  );
}
